/**
 * ProposalPerformance — Performance metrics grid
 *
 * Shows key system performance data: consumption, solar production,
 * battery cycles, and peak demand reduction.
 * Only renders metrics that have valid data (> 0).
 */

import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { vortxColors, vortxTypography } from '@vortx/shared-ui';

function getOutputs(scenario) {
  const results = scenario.last_run_results || scenario.reopt_results || {};
  const outputs = results.outputs || {};
  return outputs.Scenario?.Site || outputs;
}

function formatMWh(kwh) {
  const mwh = kwh / 1000;
  return mwh.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: mwh >= 100 ? 0 : 1,
  }) + ' MWh';
}

function computePeakReduction(outputs) {
  const loadSeries = outputs.ElectricLoad?.load_series_kw;
  const gridSeries = outputs.ElectricUtility?.electric_to_load_series_kw;

  if (!loadSeries || !gridSeries || loadSeries.length === 0 || gridSeries.length === 0) {
    return null;
  }

  // Avoid Math.max(...arr) which can cause stack overflow on 8760-element arrays
  const peakLoad = loadSeries.reduce((max, v) => v > max ? v : max, -Infinity);
  const peakGrid = gridSeries.reduce((max, v) => v > max ? v : max, -Infinity);

  if (peakLoad <= 0) return null;

  const reduction = (peakLoad - peakGrid) / peakLoad;
  return reduction > 0 ? reduction : null;
}

export default function ProposalPerformance({ scenario }) {
  const metrics = useMemo(() => {
    if (!scenario) return [];

    const outputs = getOutputs(scenario);
    const result = [];

    // Annual Consumption
    const annualKwh = outputs.ElectricLoad?.annual_calculated_kwh;
    if (annualKwh != null && annualKwh > 0) {
      result.push({
        label: 'Annual Consumption',
        value: formatMWh(annualKwh),
      });
    }

    // Solar Production
    const solarKwh = outputs.PV?.annual_energy_produced_kwh;
    if (solarKwh != null && solarKwh > 0) {
      result.push({
        label: 'Solar Production',
        value: formatMWh(solarKwh),
      });
    }

    // Battery Cycles
    const cycles = outputs.ElectricStorage?.year_one_battery_cycles;
    if (cycles != null && cycles > 0) {
      result.push({
        label: 'Battery Cycles',
        value: `${Math.round(cycles)} cycles/yr`,
      });
    }

    // Peak Demand Reduction
    const reduction = computePeakReduction(outputs);
    if (reduction != null) {
      result.push({
        label: 'Peak Demand Reduction',
        value: `${(reduction * 100).toFixed(0)}%`,
      });
    }

    return result;
  }, [scenario]);

  if (!metrics || metrics.length === 0) return null;

  return (
    <Box>
      <Typography
        variant="overline"
        sx={{
          color: vortxColors.stone,
          fontWeight: 600,
          letterSpacing: '0.1em',
          mb: 2,
          display: 'block',
        }}
      >
        System Performance
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
          gap: 2,
        }}
      >
        {metrics.map((m) => (
          <Box
            key={m.label}
            sx={{
              p: 2,
              borderRadius: 2,
              border: '1px solid rgba(255,255,255,0.08)',
              bgcolor: 'rgba(255,255,255,0.03)',
              textAlign: 'center',
            }}
          >
            <Typography
              sx={{
                fontFamily: vortxTypography.mono,
                fontWeight: 700,
                fontSize: '1.25rem',
                color: vortxColors.white,
              }}
            >
              {m.value}
            </Typography>
            <Typography variant="caption" sx={{ color: vortxColors.stone }}>
              {m.label}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}
